import type { LinkProps } from "@tanstack/react-router";

import { DIAS_PARA_AGUARDAR_RETORNO, diasNoEstado, faltaEnviar } from "./cartao-de-proposta";
import type { DefinicaoDeEtapa, ItemDaEtapa } from "./etapas";
import type { ProcessStatus, ProposalStatus, ProposalSummary, StatusTone } from "./types";

/**
 * O quadro de Propostas da tela CLIENTES (TUT-250).
 *
 * As cinco colunas que ela desenhou:
 * `PARA ELABORAÇÃO → ENVIADAS → AGUARDANDO RETORNO → APROVADAS → REPROVADAS`.
 *
 * ## `AGUARDANDO RETORNO` é RECORTE, não estado — e essa é a fatia inteira
 *
 * O domínio tem sete estados de proposta e **nenhum deles se chama "aguardando
 * retorno"**. A coluna existe no desenho mesmo assim, e a decisão registrada
 * (fase 2) diz o que ela é: *enviada há mais de N dias*. Tratá-la como estado
 * criaria um oitavo status que o negócio não tem — e, pior, um que alguém
 * teria de escrever em algum lugar, por gesto humano, para a coluna encher.
 * Sendo recorte, ela se preenche sozinha com a passagem do tempo, que é
 * exatamente o que ela quis dizer: *"mandei e ninguém respondeu"*.
 *
 * É o primo da armadilha da TUT-249 (*"não é filtro, porque a coluna não
 * muda"*, 27:16). Lá, ler um quadro como filtro implementaria a tela errada;
 * aqui, ler um recorte como estado inventaria vocabulário no banco.
 *
 * ## O mapeamento, e o que fica FORA
 *
 * Decisão 6 da fase 2: `DRAFT` → Para elaboração; `ISSUED` e `SENT` →
 * Enviadas; `ACCEPTED` → Aprovadas; `DECLINED` e `EXPIRED` → Reprovadas.
 * TUT-488: só a `SENT` COM o envio registrado, há 7 dias ou mais, passa a
 * Aguardando retorno — a emitida que ninguém enviou fica em Enviadas, com o
 * aviso "falta enviar" (ver `faltaEnviar`).
 *
 * **`CANCELLED` fica fora do quadro**, e isso é dela: proposta cancelada não é
 * "reprovada" — ninguém a recusou, ela deixou de existir. Enfiá-la em
 * Reprovadas contaria como derrota comercial o que foi desistência nossa.
 */

/** As colunas, na ordem do desenho. */
export const COLUNAS_DE_PROPOSTA = [
  "para-elaboracao",
  "enviadas",
  "aguardando-retorno",
  "aprovadas",
  "reprovadas",
] as const;

export type ColunaDeProposta = (typeof COLUNAS_DE_PROPOSTA)[number];

// A janela de "aguardando retorno" (7 dias) mora em `cartao-de-proposta.ts`.
export { DIAS_PARA_AGUARDAR_RETORNO };

/**
 * Em que coluna a proposta cai — `null` quando ela não pertence ao quadro.
 *
 * `null` é resposta legítima e não erro: é `CANCELLED`, e o chamador a
 * descarta em vez de inventar um lugar para ela.
 *
 * TUT-488 — "Aguardando retorno" é a proposta ENVIADA (com o envio registrado)
 * há 7 dias ou mais. A emitida que ninguém enviou fica em "Enviadas" com o
 * aviso "falta enviar", por mais velha que seja: o cliente não está sem
 * responder, ele nunca recebeu. O mesmo vale para a marcada como enviada sem o
 * registro do envio — não há data de envio para contar os dias.
 */
export function colunaDaProposta(
  proposta: Pick<ProposalSummary, "status" | "updatedAt" | "createdAt" | "sentAt">,
  agora: Date,
): ColunaDeProposta | null {
  switch (proposta.status) {
    case "DRAFT":
      return "para-elaboracao";
    case "ISSUED":
      return "enviadas";
    case "SENT":
      return !faltaEnviar(proposta) &&
        diasNoEstado(proposta, agora) >= DIAS_PARA_AGUARDAR_RETORNO
        ? "aguardando-retorno"
        : "enviadas";
    case "ACCEPTED":
      return "aprovadas";
    case "DECLINED":
    case "EXPIRED":
      return "reprovadas";
    // `CANCELLED` — fora do quadro, de propósito. Ver o cabeçalho.
    default:
      return null;
  }
}

/**
 * A proposta no vocabulário do quadro da casa.
 *
 * A normalização acontece na BORDA, como `paraItemDeLaudo` faz na Elaboração:
 * `ItemDaEtapa` carrega UM status, e quem traduz o vocabulário nativo de cada
 * origem é quem a conhece. Aqui isso é ainda mais necessário, porque a coluna
 * depende do RELÓGIO — e `aceita` não recebe o "agora".
 */
export function paraItemDeProposta(
  proposta: ProposalSummary,
  agora: Date,
): ItemDaEtapa | null {
  const coluna = colunaDaProposta(proposta, agora);
  if (!coluna) return null;
  return {
    id: proposta.id,
    status: coluna,
    responsavelId: null,
    // Proposta não tem prazo próprio no domínio. `null` é verdade, e mantém os
    // selos de atraso do quadro calados em vez de inventar um vencimento.
    prazoEm: null,
  };
}

/** O quadro, já no contrato que `QuadroDeEtapa` desenha. */
export const quadroDePropostas: DefinicaoDeEtapa = {
  chave: "propostas",
  titulo: "Propostas",
  colunas: [
    {
      chave: "para-elaboracao",
      titulo: "Para elaboração",
      aceita: (item) => item.status === "para-elaboracao",
    },
    {
      chave: "enviadas",
      titulo: "Enviadas",
      aceita: (item) => item.status === "enviadas",
    },
    {
      chave: "aguardando-retorno",
      titulo: "Aguardando retorno",
      aceita: (item) => item.status === "aguardando-retorno",
    },
    {
      chave: "aprovadas",
      titulo: "Aprovadas",
      aceita: (item) => item.status === "aprovadas",
    },
    {
      chave: "reprovadas",
      titulo: "Reprovadas",
      aceita: (item) => item.status === "reprovadas",
    },
  ],
};

/** O que os quatro contadores do topo contam — ver `propostasDoContador`. */
export type ContadorDeProposta =
  | "pendentes"
  | "assistencias"
  | "especiais"
  | "consultoria";

/**
 * TUT-276 — a LISTA por trás de cada um dos quatro contadores do topo, e não
 * só o número.
 *
 * `contadoresDePropostas` (abaixo) é `.length` desta função, e
 * `components/ui/pilha-de-foco.tsx` filtra por ela antes de montar a lista do
 * pop-up — assim o número do cartão e o total da lista NUNCA discordam, os
 * dois vêm do mesmo predicado.
 */
export function propostasDoContador(
  contador: ContadorDeProposta,
  propostas: ProposalSummary[],
): ProposalSummary[] {
  const emAberto: ProposalStatus[] = ["DRAFT", "ISSUED", "SENT"];
  const abertas = propostas.filter((p) => emAberto.includes(p.status));
  // TUT-488 — os três de TIPO contam só as em aberto: "Consultoria 12"
  // contava também as decididas e as canceladas, e não queria dizer "12 para
  // trabalhar". A aceita há um ano continua sendo Consultoria, mas não está
  // na fila de ninguém.
  switch (contador) {
    case "pendentes":
      return abertas;
    case "assistencias":
      return abertas.filter((p) => p.serviceType === "ASSISTANCE");
    case "especiais":
      return abertas.filter((p) => p.serviceType === "SPECIAL");
    case "consultoria":
      return abertas.filter((p) => p.serviceType === "CONSULTING");
  }
}

/**
 * Os quatro contadores do topo: `PROPOSTAS PENDENTES · ASSISTÊNCIAS ·
 * ESPECIAIS · CONSULTORIA`.
 *
 * O primeiro responde "quanto trabalho comercial está em aberto" e os outros
 * três, "de que tipo, dentro do que está em aberto" (TUT-488): os três somam
 * o primeiro. Antes eles contavam todas as propostas do tipo, decididas e
 * canceladas incluídas.
 */
export function contadoresDePropostas(propostas: ProposalSummary[]): {
  pendentes: number;
  assistencias: number;
  especiais: number;
  consultoria: number;
} {
  return {
    pendentes: propostasDoContador("pendentes", propostas).length,
    assistencias: propostasDoContador("assistencias", propostas).length,
    especiais: propostasDoContador("especiais", propostas).length,
    consultoria: propostasDoContador("consultoria", propostas).length,
  };
}

/**
 * Onde o cartão da proposta abre — e não existe uma rota só.
 *
 * Cada tipo de serviço tem o seu editor (`/propostas/assistencia`,
 * `/consultoria`, `/especial`), porque os termos de cada um são diferentes; a
 * tabela de Propostas já escolhia assim, e o cartão segue a mesma escolha em
 * vez de inventar uma terceira porta de entrada.
 *
 * TUT-276 — moveu de `CadastrosPage.tsx` para cá: `PilhaDeFoco` precisa do
 * MESMO destino para a primeira coluna do pop-up, e duas cópias divergiriam
 * no dia em que uma proposta ganhasse um quarto tipo de serviço.
 */
export function destinoDaProposta(proposta: ProposalSummary): LinkProps {
  const to =
    proposta.serviceType === "ASSISTANCE"
      ? "/propostas/assistencia"
      : proposta.serviceType === "CONSULTING"
        ? "/propostas/consultoria"
        : "/propostas/especial";
  return { to, search: { id: proposta.id } };
}

const ROTULO_DO_TIPO_DE_SERVICO: Record<ProposalSummary["serviceType"], string> = {
  ASSISTANCE: "Assistência",
  CONSULTING: "Consultoria",
  SPECIAL: "Especial",
};

const ROTULO_DA_MODALIDADE: Record<NonNullable<ProposalSummary["assistanceModality"]>, string> = {
  CONTRACT: "Contrato",
  SINGLE_EXPERTISE: "Perícia avulsa",
};

/**
 * A coluna "Modalidade" da lista de Propostas: a modalidade, na Assistência;
 * o tipo de serviço, nas outras duas (que não têm modalidade).
 *
 * TUT-472 — a ordenação da demo usa ESTA função como acessor, para a lista
 * ordenar pelo que a coluna mostra, e não pelo código cru (que é nulo fora da
 * Assistência e jogava Consultoria e Especial para o fim).
 */
export function rotuloDaModalidade(
  proposta: Pick<ProposalSummary, "serviceType" | "assistanceModality">,
): string {
  return proposta.serviceType === "ASSISTANCE" && proposta.assistanceModality
    ? ROTULO_DA_MODALIDADE[proposta.assistanceModality]
    : ROTULO_DO_TIPO_DE_SERVICO[proposta.serviceType];
}

// O MESMO vocabulário que `ReportsPage.tsx` já usa para status de proposta —
// não achei de-para único em `lib/` (cada tela que precisa tem a sua cópia
// local), e esta fatia não é o lugar para consolidar as três; ela cria a
// QUARTA cópia porque a lista do pop-up precisa do rótulo em texto, e não em
// badge colorido como as outras. TUT-472: o selo da lista de Propostas lê
// este mesmo de-para (`situacaoDaProposta`), em vez de uma quinta cópia.
const ROTULO_DO_STATUS_DA_PROPOSTA: Record<ProposalStatus, string> = {
  DRAFT: "Rascunho",
  ISSUED: "Emitida",
  SENT: "Enviada",
  ACCEPTED: "Aceita",
  DECLINED: "Recusada",
  EXPIRED: "Expirada",
  CANCELLED: "Cancelada",
};

// TUT-472 — o tom do selo de status na lista de Propostas, que desenhava
// "Rascunho" fixo para toda linha (a emitida, a aceita e a recusada também).
// Os tons seguem os de Trabalhos especiais (`SpecialWorksPage`): em aberto é
// neutro, a caminho do cliente é informativo, aceita é sucesso e a que não
// vingou é crítica. Cancelada fica neutra: foi desistência nossa, não derrota
// comercial — o mesmo raciocínio que a deixa fora do quadro.
const TOM_DO_STATUS_DA_PROPOSTA: Record<ProposalStatus, StatusTone> = {
  DRAFT: "neutral",
  ISSUED: "info",
  SENT: "info",
  ACCEPTED: "success",
  DECLINED: "critical",
  EXPIRED: "critical",
  CANCELLED: "neutral",
};

/** O selo de status da proposta, no vocabulário de `ROTULO_DO_STATUS_DA_PROPOSTA`. */
export function situacaoDaProposta(status: ProposalStatus): ProcessStatus {
  return {
    key: status,
    label: ROTULO_DO_STATUS_DA_PROPOSTA[status],
    tone: TOM_DO_STATUS_DA_PROPOSTA[status],
  };
}

/**
 * A coluna "Situação" do pop-up: tipo de serviço · status, os dois no
 * vocabulário que a casa já usa em outras telas — não é vocabulário novo.
 */
export function detalheDaProposta(
  proposta: Pick<ProposalSummary, "serviceType" | "status">,
): string {
  return `${ROTULO_DO_TIPO_DE_SERVICO[proposta.serviceType]} · ${ROTULO_DO_STATUS_DA_PROPOSTA[proposta.status]}`;
}
